import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { GiftEngine, refreshCartUI } from '@theme/gift-engine';
import { GiftChooser, giftsExhausted, hideGiftPanels } from '@theme/gift-chooser';

/**
 * BOXR gift offer (BOX-318).
 *
 * Takes over add to cart on a product that a gifting campaign applies to, so the pack
 * and its gift go in together. Lives on the product page rather than inside the bundle
 * picker, because a campaign can apply to a single product that has no pack siblings,
 * and because the cart drawer and quick add need to read the same campaign.
 *
 * This component owns the placement; what the customer may pick and what that resolves
 * to lives in `gift-chooser.js`.
 *
 * @typedef {import('./gift-chooser.js').GiftCampaign} GiftCampaign
 * @typedef {import('./gift-chooser.js').GiftProduct} GiftProduct
 */

const FEEDBACK_RESET_MS = 2000;

export class GiftOffer extends Component {
  /** @type {GiftCampaign | null} */
  #campaign = null;

  /** @type {GiftChooser | null} */
  #chooser = null;

  #submitting = false;

  #abortController = new AbortController();

  /** @type {number | null} */
  #renderedForProductId = null;

  /** @type {GiftProduct[] | null} */
  #bundleProducts = null;

  /** @type {MutationObserver | null} */
  #bundleObserver = null;

  /** @type {Map<number, GiftCampaign> | null} */
  #cardCampaigns = null;

  /** @type {GiftCampaign | null} */
  #renderedCampaign = null;

  /**
   * What the customer picked, per campaign. Switching packs builds a different chooser,
   * and a section that re-renders builds a fresh one; without this the choice would be
   * gone on the way back.
   *
   * @type {Map<string, import('./gift-chooser.js').Choices>}
   */
  #choices = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();

    if (!this.#start()) return;

    this.#interceptAddToCart();
    this.#holdTheButton();

    document.addEventListener(ThemeEvents.variantUpdate, () => this.#renderChooser(), {
      signal: this.#abortController.signal,
    });
  }

  /**
   * Keeps the section in step with the panel, and answers the button that stands in for
   * add to cart while the gift is unfinished. Pressing it is what puts the missing
   * choices on screen: the customer asked to move on, so now they are told what is left.
   */
  #holdTheButton() {
    const section = this.#section;
    if (!section) return;

    const { signal } = this.#abortController;

    section.addEventListener(
      'gift:state',
      (event) => {
        const complete = /** @type {CustomEvent} */ (event).detail?.complete;
        section.toggleAttribute('data-gift-incomplete', !complete);

        // The sticky bar sits outside this section, so it needs the flag of its own.
        this.#stickyBar?.toggleAttribute('data-gift-incomplete', !complete);
      },
      { signal }
    );

    // On the document: the prompt may be the one in the sticky bar, outside this section.
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element) || !target.closest('[data-gift-select]')) return;
        if (!this.#chooserContainer) return;

        event.preventDefault();
        this.#chooser?.markMissingChoices();
        this.#chooserContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
      },
      { signal }
    );
  }

  /** Null in quick add, where the nearest section is a collection of other products. */
  get #stickyBar() {
    if (this.closest('.quick-add-modal')) return null;

    return this.closest('.shopify-section')?.querySelector('sticky-add-to-cart') ?? null;
  }

  /**
   * Quick add fills its modal by morphing in the grid content of whichever card was
   * clicked, which keeps this element alive but replaces the config inside it. So the
   * campaign is read again and every choice made for the previous product is dropped.
   */
  updatedCallback() {
    super.updatedCallback();

    this.#rememberChoices();
    this.#chooser?.destroy();
    this.#campaign = null;
    this.#chooser = null;
    this.#bundleProducts = null;
    this.#cardCampaigns = null;
    this.#renderedCampaign = null;
    this.#renderedForProductId = null;

    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#bundleObserver?.disconnect();
  }

  /** @returns {boolean} Whether there is a campaign worth offering */
  #start() {
    this.#loadCampaign();

    // The page's own product does not have to be the one with the offer. A pack in the
    // picker carries its own campaign, so a 6-pack page holding a 3-pack that gives a
    // gift has to start as well, or that card promises something nothing can deliver.
    if (!this.#isSupported(this.#campaign) && !this.#hasCardCampaign) return false;

    this.#renderWhenReady();
    return true;
  }

  /** @returns {boolean} Whether a pack on this page carries an offer of its own */
  get #hasCardCampaign() {
    for (const campaign of this.#campaignsByProduct.values()) {
      if (this.#isSupported(campaign)) return true;
    }

    return false;
  }

  #rememberChoices() {
    const handle = this.#renderedCampaign?.handle;
    if (handle && this.#chooser) this.#choices.set(handle, this.#chooser.choices);
  }

  /** @param {GiftCampaign} campaign */
  #buildChooser(campaign) {
    return new GiftChooser({
      campaign,
      choices: this.#choices.get(campaign.handle),
      signal: this.#abortController.signal,
      texts: {
        heading: this.dataset.headingText,
        headingChoice: this.dataset.headingChoiceText,
        colorLabel: this.dataset.colorLabelText,
        sizeLabel: this.dataset.sizeLabelText,
        pickColor: this.dataset.pickColorText,
        pickSize: this.dataset.pickSizeText,
        pickColorSize: this.dataset.pickColorSizeText,
        matchedSize: this.dataset.matchedSizeText,
        matchedColor: this.dataset.matchedColorText,
        matchedBoth: this.dataset.matchedBothText,
        freeLabel: this.dataset.freeLabelText,
        errorColor: this.dataset.errorColorText,
        errorSize: this.dataset.errorSizeText,
        packSoldOut: this.dataset.packSoldOutText,
        packSoldOutNotice: this.dataset.packSoldOutNoticeText,
        allSoldOut: this.dataset.allSoldOutText,
        soldOut: this.dataset.soldOutText,
      },
      host: {
        container: () => this.#chooserContainer,
        scope: () => this.#section,
        activeProduct: () => this.#activeProduct,
        purchasedVariantId: () => this.#getSelectedVariantId(),
      },
    });
  }

  #loadCampaign() {
    const dataScript = this.querySelector('script[data-gift-campaign]');
    if (!dataScript?.textContent) return;

    try {
      this.#campaign = JSON.parse(dataScript.textContent);
    } catch (error) {
      console.error('GiftOffer: failed to parse campaign', error);
    }
  }

  /** @param {GiftCampaign | null} campaign */
  #isSupported(campaign) {
    switch (campaign?.rewardType) {
      case 'same_pack':
        return !!campaign.triggerProduct;
      case 'specific_product':
        return campaign.rewardProducts?.length >= 1;
      case 'choice_from_list':
        return campaign.rewardProducts?.length >= 2;
      default:
        return false;
    }
  }

  // ─── Placement ─────────────────────────────────────────────────────

  /**
   * On a product page the grid content, in quick add the dialog: the modal is built from
   * the grid content but drops the wrapper itself, so there is nothing else to hold on to
   * there. The section is the fallback for any other placement.
   */
  get #section() {
    return this.closest('[data-product-grid-content], .shopify-section, .quick-add-modal');
  }

  get #addToCartButton() {
    const button = this.#section?.querySelector('add-to-cart-component [ref="addToCartButton"]');
    return button instanceof HTMLElement ? button : null;
  }

  /**
   * The bundle card is where BOXR asked for the chooser, but only while that card is on
   * screen — not in quick add, where the picker never initialises, and not on a product
   * without pack siblings. Both fall back to the container in buy-buttons.liquid.
   *
   * @returns {Element | null}
   */
  get #chooserContainer() {
    const inCard = this.#section?.querySelector('.bundle-card--selected [data-gift-chooser]');

    // Ask the card whether it is on screen, not the panel: the panel starts hidden, and
    // a hidden element reports no offset parent whatever the card around it does.
    const card = inCard?.closest('.bundle-card');
    if (inCard instanceof HTMLElement && card instanceof HTMLElement && card.offsetParent) {
      return inCard;
    }

    const standalone = this.#section?.querySelector(
      '.gift-panel--standalone[data-gift-chooser]'
    );

    return standalone ?? this.#section?.querySelector('[data-gift-chooser]') ?? null;
  }

  /**
   * The pack the customer is buying: whichever card the picker has selected, which is a
   * different product from the one whose page this is.
   *
   * @returns {GiftProduct | null}
   */
  get #activeProduct() {
    const selected = this.#section?.querySelector('.bundle-card--selected');
    const id = Number(selected?.getAttribute('data-product-id') ?? '');
    const fromPicker = Number.isFinite(id) && id > 0 ? this.#bundleProduct(id) : null;

    return fromPicker ?? this.#campaign?.triggerProduct ?? null;
  }

  /**
   * @param {number} id
   * @returns {GiftProduct | null}
   */
  #bundleProduct(id) {
    if (!this.#bundleProducts) {
      const script = this.#section?.querySelector('script[data-bundle-data]');

      try {
        const data = JSON.parse(script?.textContent ?? 'null');
        this.#bundleProducts = (data?.bundles ?? []).map(
          (/** @type {any} */ bundle) => ({
            id: bundle.id,
            handle: bundle.handle,
            title: bundle.title,
            image: bundle.featuredImage,
            price: bundle.variants?.[0]?.priceFormatted,
            optionNames: bundle.options ?? [],
            variants: bundle.variants ?? [],
          })
        );
      } catch (error) {
        console.error('GiftOffer: failed to parse bundle data', error);
        this.#bundleProducts = [];
      }
    }

    const product = this.#bundleProducts.find((candidate) => candidate.id === id);
    return product?.variants.length ? product : null;
  }

  /** @returns {number | null} */
  #getSelectedVariantId() {
    const input = this.#section?.querySelector('product-form-component input[name="id"]');
    if (!(input instanceof HTMLInputElement)) return null;

    const id = Number(input.value);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  // ─── Keeping the chooser on screen ─────────────────────────────────

  /**
   * Quick add morphs its modal together piece by piece, so this element can be connected
   * before the card that holds its chooser exists.
   */
  #renderWhenReady() {
    this.#renderChooser();
    this.#watchBundleSelection();

    if (this.#chooserContainer) return;

    const host = this.#section ?? document.body;
    const observer = new MutationObserver(() => {
      if (!this.#chooserContainer) return;

      observer.disconnect();
      this.#renderChooser();
      this.#watchBundleSelection();
    });

    observer.observe(host, { childList: true, subtree: true });
    this.#abortController.signal.addEventListener('abort', () => observer.disconnect());
  }

  #renderChooser() {
    const campaign = this.#campaignForActivePack;

    if (campaign !== this.#renderedCampaign) {
      this.#rememberChoices();
      this.#chooser?.destroy();
      this.#renderedCampaign = campaign;
      this.#chooser = campaign ? this.#buildChooser(campaign) : null;
    }

    if (this.#chooser) this.#chooser.render();
    else hideGiftPanels(this.#section);

    this.#hideExhaustedLabels();

    this.#renderedForProductId = this.#activeProduct?.id ?? null;
  }

  /**
   * The campaign of the pack that is selected, which is not always the one this page is
   * about: the picker swaps packs without leaving the page, and every pack brings its own
   * action. A pack without one gets no offer at all, because a gift its discount does not
   * cover would be added and then taken straight back out of the cart.
   *
   * @returns {GiftCampaign | null}
   */
  get #campaignForActivePack() {
    const active = this.#activeProduct?.id;
    if (!active) return null;

    const fromCard = this.#campaignsByProduct.get(active);
    if (fromCard) return this.#isSupported(fromCard) ? fromCard : null;

    // Without a picker there are no cards, and the page's own campaign is the only one.
    const targeted = (this.#campaign?.triggerProductIds ?? []).includes(active);
    return targeted ? this.#campaign : null;
  }

  /**
   * Takes down the label of a campaign that has nothing left to hand over. Liquid draws
   * those labels and cannot see stock, so the page would otherwise promise a free gift
   * right above the line saying there is none.
   */
  #hideExhaustedLabels() {
    const section = this.#section;
    if (!section) return;

    for (const [productId, campaign] of this.#campaignsByProduct) {
      const card = section
        .querySelector(`.bundle-card__input[value="${productId}"]`)
        ?.closest('.bundle-card');
      const label = card?.querySelector('.bundle-badge--gift');
      if (label instanceof HTMLElement) label.hidden = giftsExhausted(campaign);
    }

    // The price and the sticky bar speak for whichever pack is selected. The bar sits
    // outside the product grid, so those two are looked up from the section around it —
    // except in quick add, where the section is a whole collection of other products.
    const exhausted = giftsExhausted(this.#campaignForActivePack);
    const wider = this.closest('.quick-add-modal') ? section : (this.closest('.shopify-section') ?? section);
    const elsewhere = wider.querySelectorAll(
      'product-price .bundle-badge--gift, .sticky-add-to-cart__price .bundle-badge--gift'
    );

    for (const label of elsewhere) {
      if (label instanceof HTMLElement) label.hidden = exhausted;
    }
  }

  /** @returns {Map<number, GiftCampaign>} */
  get #campaignsByProduct() {
    if (this.#cardCampaigns?.size) return this.#cardCampaigns;

    this.#cardCampaigns = new Map();

    for (const script of this.#section?.querySelectorAll('script[data-gift-campaign-for]') ?? []) {
      const id = Number(script.getAttribute('data-gift-campaign-for'));

      try {
        this.#cardCampaigns.set(id, JSON.parse(script.textContent ?? ''));
      } catch (error) {
        console.error('GiftOffer: failed to parse a card campaign', error);
      }
    }

    return this.#cardCampaigns;
  }

  /**
   * The bundle picker switches packs by moving a class around, without an event of its
   * own, so the swap is observed instead. Only a different pack re-renders: the chooser's
   * own selected classes land in the same subtree and would otherwise loop.
   */
  #watchBundleSelection() {
    this.#bundleObserver?.disconnect();

    const picker = this.#section?.querySelector('bundle-picker-component');
    if (!picker) return;

    const observer = new MutationObserver(() => {
      if ((this.#activeProduct?.id ?? null) === this.#renderedForProductId) return;

      this.#chooser?.dropUnofferedChoices();
      this.#renderChooser();
    });

    observer.observe(picker, { attributes: true, subtree: true, attributeFilter: ['class'] });
    this.#bundleObserver = observer;
  }

  // ─── Adding to cart ────────────────────────────────────────────────

  /**
   * Caught on the document rather than on the button itself. Quick add and the cart
   * drawer both redraw through replaced markup, which throws away a listener bound to a
   * button while this element lives on. Comparing against our own button keeps a card's
   * modal from answering for the product page behind it.
   */
  #interceptAddToCart() {
    document.addEventListener(
      'click',
      (event) => {
        if (this.#submitting) return;
        if (!(event.target instanceof Element)) return;

        const clicked = event.target.closest('add-to-cart-component [ref="addToCartButton"]');
        if (!clicked || clicked !== this.#addToCartButton) return;

        const paidVariantId = this.#getSelectedVariantId();
        const giftVariantId = this.#chooser?.resolveGiftVariantId() ?? null;

        // No gift to give, so the theme's own add to cart is left alone: BOXR asked for
        // a page that never blocks on the gift.
        if (!paidVariantId || !giftVariantId) return;

        event.preventDefault();
        this.#submit(paidVariantId, giftVariantId);
      },
      { capture: true, signal: this.#abortController.signal }
    );
  }

  /**
   * @param {number} paidVariantId
   * @param {number} giftVariantId
   */
  async #submit(paidVariantId, giftVariantId) {
    this.#submitting = true;
    const showResult = this.#startFeedback();

    const result = await GiftEngine.addPair({
      paidVariantId,
      giftVariantId,
      fallback: this.#campaignForActivePack?.fallback ?? null,
    });

    if (result.ok) {
      await refreshCartUI('gift-offer');
      showResult(this.dataset.addedText ?? '');
      return;
    }

    if (result.status === 'not_discounted') {
      await refreshCartUI('gift-offer');
      showResult(this.dataset.unavailableText ?? '');
      return;
    }

    showResult(this.dataset.errorText ?? '');
  }

  /** @returns {(message: string) => void} */
  #startFeedback() {
    const button = this.#addToCartButton;
    const textSpan = button?.querySelector('.add-to-cart-text__content span');
    const originalText = textSpan?.textContent ?? '';

    if (button instanceof HTMLButtonElement) button.disabled = true;
    if (textSpan) textSpan.textContent = '...';

    return (message) => {
      if (textSpan) textSpan.textContent = message;
      setTimeout(() => {
        if (textSpan) textSpan.textContent = originalText;
        if (button instanceof HTMLButtonElement) button.disabled = false;
        this.#submitting = false;
      }, FEEDBACK_RESET_MS);
    };
  }
}

if (!customElements.get('gift-offer-component')) {
  customElements.define('gift-offer-component', GiftOffer);
}
